import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { PlatformPreviewProps } from '../types';
import { PLATFORM_CONFIG, truncateForPlatform } from '../platformConfig';
import { PreviewAvatar } from '../components/PreviewAvatar';
import { PreviewCaption } from '../components/PreviewCaption';
import { MediaDisplay } from '../components/MediaDisplay';
import { EmptyState } from '../components/EmptyState';

export const FacebookPreview = ({ caption, media, isCarousel, channel, activeIndex, onIndexChange }: PlatformPreviewProps) => {
  const { t } = useTranslation('common');
  const config = PLATFORM_CONFIG.facebook;
  const displayCaption = truncateForPlatform(caption, 'facebook');
  const isEmpty = !caption && media.length === 0;

  const displayName = channel?.displayName ?? '';

  if (isEmpty) return <EmptyState />;

  return (
    <div className="bg-white rounded-lg border border-gray-200 w-full min-w-0 max-w-[500px] mx-auto shadow-sm">
      {/* Header */}
      <div className="p-3 flex items-start gap-3">
        <PreviewAvatar colorClass={config.avatarColor} size="md" avatarUrl={channel?.avatarUrl} initials={channel?.initials} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-[15px]">{displayName}</p>
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <span>{t('preview_just_now')}</span>
            <span>-</span>
            <Icon name={ICONS.GLOBE} size="xs" color="text-gray-500" />
          </div>
        </div>
        <button type="button" className="text-gray-400 hover:text-gray-600 text-xl leading-none">
          ...
        </button>
      </div>

      {/* Caption */}
      {caption && (
        <div className="px-3 pb-3">
          <PreviewCaption
            caption={displayCaption}
            hashtagColor={config.hashtagColor}
            className="text-[15px] text-gray-900"
            truncated={caption.length > 150}
          />
        </div>
      )}

      {/* Media */}
      {media.length > 0 && (
        <MediaDisplay
          media={isCarousel ? media : media.slice(0, 1)}
          aspectRatio="auto"
          rounded="none"
          showCarouselDots={isCarousel && media.length > 1}
          activeIndex={activeIndex}
          onIndexChange={onIndexChange}
        />
      )}

      {/* Action Buttons */}
      <div className="px-1 py-1 flex items-center justify-around border-t border-gray-200">
        <button
          type="button"
          className="flex items-center justify-center gap-1 text-gray-600 hover:bg-gray-100 py-1.5 px-1 rounded-lg transition-colors flex-auto min-w-0"
        >
          <Icon name={ICONS.THUMBS_UP} size="xs" className="flex-shrink-0" />
          <span className="text-xs font-medium truncate">{t('preview_fb_like')}</span>
        </button>
        <button
          type="button"
          className="flex items-center justify-center gap-1 text-gray-600 hover:bg-gray-100 py-1.5 px-1 rounded-lg transition-colors flex-auto min-w-0"
        >
          <Icon name={ICONS.CHAT} size="xs" className="flex-shrink-0" />
          <span className="text-xs font-medium truncate">{t('preview_fb_comment')}</span>
        </button>
        <button
          type="button"
          className="flex items-center justify-center gap-1 text-gray-600 hover:bg-gray-100 py-1.5 px-1 rounded-lg transition-colors flex-auto min-w-0"
        >
          <Icon name={ICONS.SHARE} size="xs" className="flex-shrink-0" />
          <span className="text-xs font-medium truncate">{t('preview_fb_share')}</span>
        </button>
      </div>
    </div>
  );
};
